import {
  SwayyProductActivityLog,
  SwayyProductActivityLogChangeDetail,
} from "./SwayyProductActivityLog";

const GENERIC_COUNT_FIELDS = new Set([
  "before",
  "after",
  "images",
  "variants",
  "categories",
  "options",
  "prices",
  "sections",
  "feature_details",
]);

const PRICE_FIELDS: Array<{
  field: "mrp" | "sale_price" | "min_qty";
  label: string;
}> = [
  { field: "mrp", label: "MRP" },
  { field: "sale_price", label: "Sale price" },
  { field: "min_qty", label: "Min qty" },
];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const isGenericCountDetail = (
  detail: SwayyProductActivityLogChangeDetail,
): boolean => {
  if (GENERIC_COUNT_FIELDS.has(detail.field)) return true;
  const looksCount = (value: string | number | boolean | null) =>
    typeof value === "string" && /\d+\s+item\(s\)/.test(value);
  return looksCount(detail.from) || looksCount(detail.to);
};

const displayValue = (
  value: unknown,
): string | number | boolean | null => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      const numeric = Number(trimmed);
      if (Number.isFinite(numeric)) return numeric;
    }
    return value;
  }
  return null;
};

const valuesEqual = (left: unknown, right: unknown): boolean =>
  String(displayValue(left)) === String(displayValue(right));

const snapshotPrice = (
  source: unknown,
): Record<string, unknown> | null => {
  if (Array.isArray(source)) {
    const first = source[0];
    return isPlainObject(first) ? first : null;
  }
  if (!isPlainObject(source)) return null;
  if (Array.isArray(source.prices)) {
    const first = source.prices[0];
    return isPlainObject(first) ? first : null;
  }
  if (source.mrp != null || source.sale_price != null) return source;
  return null;
};

const extractPriceDetails = (
  changes: Record<string, unknown> | null,
): SwayyProductActivityLogChangeDetail[] => {
  if (!changes) return [];
  const before = snapshotPrice(changes.before);
  const after = snapshotPrice(changes.after);
  if (!before && !after) return [];

  const details: SwayyProductActivityLogChangeDetail[] = [];
  PRICE_FIELDS.forEach(({ field, label }) => {
    const fromValue = before?.[field];
    const toValue = after?.[field];
    if (valuesEqual(fromValue, toValue)) return;
    details.push({
      field,
      label,
      from: displayValue(fromValue),
      to: displayValue(toValue),
    });
  });
  return details;
};

const isEmptyDisplay = (value: string | number | boolean | null): boolean =>
  value === null || value === undefined || value === "";

const detailsForAction = (
  action: string,
  details: SwayyProductActivityLogChangeDetail[],
): SwayyProductActivityLogChangeDetail[] => {
  if (!action.includes("delete")) return details;
  return details.filter(
    (detail) => !isEmptyDisplay(detail.from) || isEmptyDisplay(detail.to),
  );
};

const mergeDetails = (
  existing: SwayyProductActivityLogChangeDetail[],
  extras: SwayyProductActivityLogChangeDetail[],
): SwayyProductActivityLogChangeDetail[] => {
  const merged = existing.filter((detail) => !isGenericCountDetail(detail));
  extras.forEach((detail) => {
    if (merged.some((item) => item.field === detail.field)) return;
    merged.push(detail);
  });
  return merged;
};

export const enrichSwayyProductActivityLog = (
  log: SwayyProductActivityLog,
): SwayyProductActivityLog => {
  try {
    return {
      ...log,
      changeDetails: detailsForAction(
        log.action,
        mergeDetails(log.changeDetails, extractPriceDetails(log.changes)),
      ),
    };
  } catch {
    return {
      ...log,
      changeDetails: detailsForAction(
        log.action,
        log.changeDetails.filter((detail) => !isGenericCountDetail(detail)),
      ),
    };
  }
};
